import React, { useEffect, useState } from 'react';
import { Table, Form, Select, Button, PopConfirm, Input } from '@maxtropy/components';
import { Col, FormInstance, Row } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { apiV2EnergyOverviewMediumByEnergyUnitPost } from '@maxtropy/device-customer-apis-v2';
import BindEnergyUnitModal, { AnalysisUnit, SelectedEnergyUnit } from '../BindEnergyUnitModal';
import { EnergyMediumSceneIds } from '@/api/energyMedium';

export interface AreaListItem {
  id: string; // 唯一标识
  areaName?: string;
  energyUnitId: number;
  energyUnitName: string;
  energyUnitGroupId: number;
  energyUnitGroupName: string;
  electricMeter?: number; // 用电计量
  waterMeter?: number; // 用水计量
  gasMeters?: number[]; // 用气计量（多选）
}

interface Option {
  label: string | number;
  value: string | number | undefined;
}

interface AreaListTableProps {
  isEdit: boolean;
  unitGroup: AnalysisUnit[];
  form: FormInstance<any>;
  onLoadingChange?: (loading: boolean) => void;
}

const AreaListTable: React.FC<AreaListTableProps> = ({ isEdit, unitGroup, form, onLoadingChange }) => {
  const [bindModalOpen, setBindModalOpen] = useState<boolean>(false);
  const [areaListData, setAreaListData] = useState<AreaListItem[]>([]);
  const [mediumOptionsMap, setMediumOptionsMap] = useState<
    Record<number, { electric: Option[]; water: Option[]; gas: Option[] }>
  >({});

  const watchedAreaList = Form.useWatch('areaList', form);

  useEffect(() => {
    if (watchedAreaList) {
      setAreaListData(watchedAreaList);
      // 加载每个用能单元的介质选项
      watchedAreaList.forEach((item: AreaListItem) => {
        if (item.energyUnitId && !mediumOptionsMap[item.energyUnitId]) {
          loadMediumOptions(item.energyUnitId);
        }
      });
    }
  }, [watchedAreaList]);

  const loadMediumOptions = (energyUnitId: number) => {
    onLoadingChange?.(true);
    apiV2EnergyOverviewMediumByEnergyUnitPost({
      list: [energyUnitId],
    })
      .then(res => {
        const list = res.list || [];
        // 分类介质：电、水、气（根据 sceneId）
        const electricOptions: Option[] = [];
        const waterOptions: Option[] = [];
        const gasOptions: Option[] = [];

        list.forEach(item => {
          const option: Option = { label: item.energyName || '', value: item.id };
          const sceneId = item.sceneId;

          // 根据 sceneId 判断能源类型（注意：ELEC 是字符串 'elec'，其他是数字）
          if (String(sceneId) === 'elec') {
            // 电能
            electricOptions.push(option);
          } else if (sceneId === EnergyMediumSceneIds.WATER_SUPPLY_AND_DRAINAGE) {
            // 给排水/用水
            waterOptions.push(option);
          } else if (
            sceneId === EnergyMediumSceneIds.GAS_SUPPLY_AND_CONSUMPTION ||
            sceneId === EnergyMediumSceneIds.HOT_STEAM ||
            sceneId === EnergyMediumSceneIds.COMPRESSED_AIR ||
            sceneId === EnergyMediumSceneIds.INDUSTRIAL_GASES
          ) {
            // 气类（供气、热蒸汽、压缩空气、工业气体）
            gasOptions.push(option);
          } else {
            // 其他类型根据能源名称判断
            if (item.energyName?.includes('电')) {
              electricOptions.push(option);
            } else if (item.energyName?.includes('水')) {
              waterOptions.push(option);
            } else if (item.energyName?.includes('气')) {
              gasOptions.push(option);
            }
          }
        });

        setMediumOptionsMap(prev => ({
          ...prev,
          [energyUnitId]: {
            electric: electricOptions,
            water: waterOptions,
            gas: gasOptions,
          },
        }));
      })
      .catch(() => undefined)
      .finally(() => onLoadingChange?.(false));
  };

  const handleAddUnits = (units: SelectedEnergyUnit[]) => {
    const currentList = form.getFieldValue('areaList') || [];
    const newItems: AreaListItem[] = units.map(unit => ({
      id: `${Date.now()}-${unit.energyUnitId}`,
      areaName: unit.energyUnitName,
      energyUnitId: unit.energyUnitId,
      energyUnitName: unit.energyUnitName,
      energyUnitGroupId: unit.energyUnitGroupId,
      energyUnitGroupName: unit.energyUnitGroupName,
      electricMeter: undefined,
      waterMeter: undefined,
      gasMeters: [],
    }));

    const newList = [...currentList, ...newItems];
    form.setFieldValue('areaList', newList);
    setAreaListData(newList);

    // 加载新添加单元的介质选项
    newItems.forEach(item => {
      if (item.energyUnitId) {
        loadMediumOptions(item.energyUnitId);
      }
    });
  };

  const handleDelete = (index: number) => {
    const currentList = form.getFieldValue('areaList') || [];
    const newList = currentList.filter((_: any, i: number) => i !== index);
    form.setFieldValue('areaList', newList);
    setAreaListData(newList);
  };

  const columns = [
    {
      title: '区域名称',
      dataIndex: 'areaName',
      width: 200,
      render: (_: string, __: AreaListItem, index: number) => {
        return (
          <Form.Item name={['areaList', index, 'areaName']} style={{ marginBottom: 0 }}>
            <Input disabled={!isEdit} placeholder="请输入" style={{ width: 180 }} />
          </Form.Item>
        );
      },
    },
    {
      title: '用能单元',
      dataIndex: 'energyUnitName',
      width: 200,
      render: (text: string) => {
        return <span>{text}</span>;
      },
    },
    {
      title: '用电计量',
      dataIndex: 'electricMeter',
      width: 200,
      render: (_: any, record: AreaListItem, index: number) => {
        const options = mediumOptionsMap[record.energyUnitId]?.electric || [];
        return (
          <Form.Item name={['areaList', index, 'electricMeter']} style={{ marginBottom: 0 }}>
            <Select
              disabled={!isEdit}
              placeholder="请选择"
              options={options}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '用水计量',
      dataIndex: 'waterMeter',
      width: 200,
      render: (_: any, record: AreaListItem, index: number) => {
        const options = mediumOptionsMap[record.energyUnitId]?.water || [];
        return (
          <Form.Item name={['areaList', index, 'waterMeter']} style={{ marginBottom: 0 }}>
            <Select
              disabled={!isEdit}
              placeholder="请选择"
              options={options}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '用气计量',
      dataIndex: 'gasMeters',
      width: 200,
      render: (_: any, record: AreaListItem, index: number) => {
        const options = mediumOptionsMap[record.energyUnitId]?.gas || [];
        return (
          <Form.Item name={['areaList', index, 'gasMeters']} style={{ marginBottom: 0 }}>
            <Select
              disabled={!isEdit}
              placeholder="请选择"
              options={options}
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: 180 }}
              maxTagCount={2}
            />
          </Form.Item>
        );
      },
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 100,
      render: (_: any, record: AreaListItem, index: number) => {
        return (
          <PopConfirm title="确定删除该区域吗？" onConfirm={() => handleDelete(index)}>
            <Button disabled={!isEdit} type="link">
              删除
            </Button>
          </PopConfirm>
        );
      },
    },
  ];

  return (
    <Row gutter={8}>
      <Col span={24}>
        <div style={{ marginBottom: 8 }}>
          <Button type="primary" disabled={!isEdit} icon={<PlusOutlined />} onClick={() => setBindModalOpen(true)}>
            新增配置
          </Button>
        </div>
        <Form.Item noStyle name="areaList">
          <Table rowKey="id" columns={columns} dataSource={areaListData} pagination={false} scroll={{ x: 1100 }} />
        </Form.Item>
      </Col>
      {bindModalOpen && (
        <BindEnergyUnitModal
          open={bindModalOpen}
          setOpen={setBindModalOpen}
          isEdit={isEdit}
          unitGroup={unitGroup}
          existingUnits={areaListData}
          onConfirm={handleAddUnits}
        />
      )}
    </Row>
  );
};

export default AreaListTable;
